<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所任务：负责人须为案件成员。可完成/取消。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="待办" value="OPEN" />
          <el-option label="已完成" value="DONE" />
          <el-option label="已取消" value="CANCELLED" />
        </el-select>
      </el-form-item>
      <el-form-item label="仅逾期">
        <el-switch v-model="query.overdueOnly" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">新建任务</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="120" />
      <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
      <el-table-column prop="assigneeLawyerName" label="负责人" width="100" />
      <el-table-column prop="dueDate" label="到期日" width="110" />
      <el-table-column prop="status" label="状态" width="90" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <template v-if="row.status === 'OPEN'">
            <el-button link type="success" @click="doComplete(row)">完成</el-button>
            <el-button link type="danger" @click="doCancel(row)">取消</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="新建任务" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%">
            <el-option
              v-for="m in matterOptions"
              :key="m.id"
              :label="`${m.firmCaseNo || m.id} · ${m.title}`"
              :value="m.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" maxlength="128" />
        </el-form-item>
        <el-form-item label="负责人" prop="assigneeLawyerId">
          <el-select v-model="form.assigneeLawyerId" filterable class="!w-100%">
            <el-option
              v-for="l in lawyerOptions"
              :key="l.id"
              :label="`${l.name || '律师'}（#${l.id}）`"
              :value="l.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="到期日">
          <el-date-picker v-model="form.dueDate" type="date" value-format="YYYY-MM-DD" class="!w-100%" />
        </el-form-item>
        <el-form-item label="内容">
          <el-input v-model="form.content" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCreate">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import {
  FirmPracticeApi,
  type PracticeMatterVO,
  type PracticeTaskVO
} from '@/api/firm/practice'
import { FirmPortalApi, type FirmLawyerVO } from '@/api/firm/portal'

defineOptions({ name: 'FirmPracticeTask' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeTaskVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  caseId: undefined as number | undefined,
  status: undefined as string | undefined,
  overdueOnly: false
})

const matterOptions = ref<PracticeMatterVO[]>([])
const lawyerOptions = ref<FirmLawyerVO[]>([])
const createVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  caseId: undefined as number | undefined,
  title: '',
  assigneeLawyerId: undefined as number | undefined,
  dueDate: undefined as string | undefined,
  content: ''
})
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  assigneeLawyerId: [{ required: true, message: '请选择负责人', trigger: 'change' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.taskPage({
      ...query,
      overdueOnly: query.overdueOnly || undefined
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const openCreate = async () => {
  form.caseId = undefined
  form.title = ''
  form.assigneeLawyerId = undefined
  form.dueDate = undefined
  form.content = ''
  createVisible.value = true
  const [matters, lawyers] = await Promise.all([
    FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 }),
    FirmPortalApi.lawyerAssignableList()
  ])
  matterOptions.value = matters?.list || []
  lawyerOptions.value = lawyers || []
}

const submitCreate = async () => {
  await formRef.value?.validate()
  if (!form.caseId || !form.assigneeLawyerId) return
  saving.value = true
  try {
    await FirmPracticeApi.taskCreate({
      caseId: form.caseId,
      title: form.title.trim(),
      assigneeLawyerId: form.assigneeLawyerId,
      dueDate: form.dueDate,
      content: form.content || undefined
    })
    message.success('已创建')
    createVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const doComplete = async (row: PracticeTaskVO) => {
  if (!row.id) return
  await FirmPracticeApi.taskComplete(row.id)
  message.success('已完成')
  await getList()
}

const doCancel = async (row: PracticeTaskVO) => {
  if (!row.id) return
  try {
    await message.confirm('确认取消该任务？')
  } catch {
    return
  }
  await FirmPracticeApi.taskCancel(row.id)
  message.success('已取消')
  await getList()
}

onMounted(getList)
</script>
